import { Injectable } from '@angular/core';
import { CustomerComplaintCountsApiService } from '@app/shared/services/api/customer-complaint-counts.service';
import { StateService } from '@app/shared/services/state/state.service';
import { UserMunicipalitiesService } from '@app/shared/services/user-municipalities.service';
import { Observable, combineLatest, map, first, forkJoin, of, tap } from 'rxjs';
import { CustomerComplaintCount } from '../models/customer-complaint-counts';
import { CustomerComplaintCountsState, initialCustomerComplaintCountsState } from '../models/customer-complaint-counts.state';
import { CustomerComplaintImportResult } from '../models/customer-complaint-import';

@Injectable({
  providedIn: 'root',
})
export class CustomerComplaintCountsStateService extends StateService<CustomerComplaintCountsState> {
  public customerComplaintCounts$: Observable<CustomerComplaintCount[]> = this.select((state: CustomerComplaintCountsState) => state.complaintCounts);

  public isLoading$: Observable<boolean> = this.select((state: CustomerComplaintCountsState) => state.isLoading);

  public currentMunicipalityComplaintCount$: Observable<CustomerComplaintCount | undefined>;

  public get complaintCounts(): CustomerComplaintCount[] {
    return this.state.complaintCounts;
  }

  public get isLoaded(): boolean {
    return !this.state.isLoading;
  }

  constructor(
    private readonly customerComplaintCountsApiService: CustomerComplaintCountsApiService,
    private readonly userMunicipalitiesService: UserMunicipalitiesService
  ) {
    super(initialCustomerComplaintCountsState);

    this.currentMunicipalityComplaintCount$ = combineLatest({
      counts: this.customerComplaintCounts$,
      municipality: this.userMunicipalitiesService.currentMunicipality$,
    }).pipe(map(({ counts, municipality }) => counts.find((count) => count.municipality?.municipalityKey === municipality?.key)));
  }

  public load(): void {
    this.setState({
      ...this.state,
      isLoading: true,
    });

    this.customerComplaintCountsApiService
      .getAll()
      .pipe(first())
      .subscribe({
        next: (complaintCounts: CustomerComplaintCount[]) => {
          this.setState({
            complaintCounts,
            isLoading: false,
          });
        },
        error: () => {
          this.setState({
            complaintCounts: [],
            isLoading: false,
          });
        },
      });
  }

  public refresh(): void {
    this.load();
  }

  public getByMunicipalityGuid(municipalityGuid: string): CustomerComplaintCount | undefined {
    return this.complaintCounts.find((count) => count.municipality?.guid === municipalityGuid);
  }

  public getByMunicipalityKey(municipalityKey: string): CustomerComplaintCount | undefined {
    return this.complaintCounts.find((count) => count.municipality?.municipalityKey === municipalityKey);
  }

  public saveImportRows(result: CustomerComplaintImportResult): Observable<void> {
    const requests: Observable<unknown>[] = [];

    if (result.createDtos.length > 0) {
      requests.push(this.customerComplaintCountsApiService.create(result.createDtos));
    }

    if (result.patchDtos.length > 0) {
      requests.push(this.customerComplaintCountsApiService.patch(result.patchDtos));
    }

    if (result.deleteDtos.length > 0) {
      requests.push(forkJoin(result.deleteDtos.map((guid) => this.customerComplaintCountsApiService.delete(guid))));
    }

    if (requests.length === 0) {
      return of(void 0);
    }

    return forkJoin(requests).pipe(
      tap(() => this.refresh()),
      map(() => void 0)
    );
  }
}
